// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import { type ProductPool, type ProductPoolId, type UserId, useEvolvePost } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { WrappedMultiSelect } from 'components/Mantine/TypeSafeSelect/WrappedMultiSelect';
import { useUser } from 'hooks/user/useUser';

type Props = {
  userIds: UserId[];
  productPools: ProductPool[];
  loadingProductPools: boolean;
  opened: boolean;
  onClose: () => void;
  refresh: () => void;
};

export const AddLicensesModal = ({ userIds, productPools, loadingProductPools, opened, onClose, refresh }: Props) => {
  const noAssignableLicenses = !loadingProductPools && productPools.length === 0;

  const { user, refreshUser } = useUser();
  const { apiCall: addLicensesApiCall, loading: savingLicenses } = useEvolvePost('admin/entitlement');
  const [productPoolIds, setProductPoolIds] = useState<ProductPoolId[]>([]);

  useEffect(() => {
    if (!opened) {
      setProductPoolIds([]);
    }
  }, [opened]);

  const onSubmit = async () => {
    if (noAssignableLicenses) {
      onClose();
      return;
    }
    await addLicensesApiCall({ productPoolIds, userIds });
    notifications.show({
      title: 'Successfully added',
      message: `Added license${productPoolIds.length === 1 ? '' : 's'} to ${userIds.length} member${
        userIds.length === 1 ? '' : 's'
      }`,
      color: 'green',
    });
    refresh();
    if (userIds.includes(user.userId)) {
      void refreshUser();
    }
  };

  if (noAssignableLicenses) {
    return (
      <WrappedModal
        title="No assignable licenses"
        titleIcon="Info"
        titleIconColor="primary"
        withCancelButton={false}
        opened={opened}
        onClose={onClose}
        onConfirm={onClose}
      >
        <Text>You have no available user licenses.</Text>
      </WrappedModal>
    );
  }

  return (
    <WrappedModal
      title="Assign licenses"
      subtitle={`${userIds.length} member${userIds.length === 1 ? '' : 's'} selected`}
      opened={opened}
      onClose={onClose}
      onConfirm={onSubmit}
      disabled={productPoolIds.length === 0}
      loading={savingLicenses}
      confirmationText="Assign"
    >
      <WrappedMultiSelect
        label="Products"
        nowrap={false}
        placeholder={
          loadingProductPools ? 'Loading...' : productPools.length === 0 ? 'No licenses found' : 'Select licenses...'
        }
        value={productPoolIds}
        onChange={setProductPoolIds}
        data={productPools.map((p) => ({
          label: `${p.product.productNameFormatted} (${p.poolAvailableQuantity} available)`,
          value: p.productPoolId,
        }))}
        disabled={savingLicenses || productPools.length === 0}
      />
    </WrappedModal>
  );
};
